import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { Timestamp } from 'firebase/firestore';

/**
 * Convierte valores Timestamp de Firestore a formato ISO string
 */
function convertTimestampsToISO(obj: any): any {
    if (obj === null || obj === undefined) {
        return obj;
    }

    if (obj instanceof Timestamp) {
        return obj.toDate().toISOString();
    }

    if (obj instanceof Date) {
        return obj.toISOString();
    }

    if (Array.isArray(obj)) {
        return obj.map(item => convertTimestampsToISO(item));
    }

    if (typeof obj === 'object') {
        const converted: any = {};
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                converted[key] = convertTimestampsToISO(obj[key]);
            }
        }
        return converted;
    }

    return obj;
}

/**
 * Obtiene la estructura completa para iterar por todas las regiones
 */
async function getFullStructure(): Promise<any> {
    try {
        const structureRef = doc(db, 'structure', 'main');
        const structureDoc = await getDoc(structureRef);

        if (!structureDoc.exists()) {
            return { regions: {} };
        }

        return structureDoc.data();
    } catch (error) {
        console.error('Error getting structure:', error);
        return { regions: {} };
    }
}

/**
 * Exporta toda la colección 'data' de Firestore
 * Organizada por región -> país -> cliente -> acciones
 */
export async function exportFirestoreData(): Promise<void> {
    try {
        const exportData: any = {
            exportDate: new Date().toISOString(),
            data: {}
        };

        // Obtener la estructura para saber qué regiones/países/clientes existen
        const structure = await getFullStructure();

        if (!structure.regions) {
            console.warn('No regions found in structure');
            // Intentar listar regiones directamente desde data collection
            const dataRef = collection(db, 'data');
            const regionsSnapshot = await getDocs(dataRef);

            // Iterar por cada región encontrada
            for (const regionDoc of regionsSnapshot.docs) {
                const regionId = regionDoc.id;
                exportData.data[regionId] = await exportRegionData(regionId);
            }
        } else {
            // Iterar por cada región en la estructura
            for (const [regionId] of Object.entries(structure.regions)) {
                exportData.data[regionId] = await exportRegionData(regionId);
            }
        }

        // Convertir todos los Timestamps a ISO strings
        const cleanedData = convertTimestampsToISO(exportData);

        // Descargar como JSON
        downloadJSON(cleanedData, `firestore_data_export_${Date.now()}.json`);

    } catch (error) {
        console.error('Error exporting Firestore data:', error);
        throw error;
    }
}

/**
 * Exporta todos los datos de una región específica
 */
async function exportRegionData(regionId: string): Promise<any> {
    const regionData: any = {
        countries: {}
    };

    try {
        const countriesRef = collection(db, 'data', regionId, 'countries');
        const countriesSnapshot = await getDocs(countriesRef);

        // Iterar por cada país
        for (const countryDoc of countriesSnapshot.docs) {
            const countryId = countryDoc.id;
            const countryInfo = countryDoc.data();

            regionData.countries[countryId] = {
                info: {
                    id: countryInfo.id || countryId,
                    name: countryInfo.name || countryId,
                    isoCode: countryInfo.isoCode || '',
                    lastRowNumber: countryInfo.lastRowNumber || 0
                },
                clients: await exportCountryClients(regionId, countryId)
            };
        }
    } catch (error) {
        console.error(`Error exporting region ${regionId}:`, error);
    }

    return regionData;
}

/**
 * Exporta todos los clientes de un país
 */
async function exportCountryClients(regionId: string, countryId: string): Promise<any> {
    const clientsData: any = {};

    try {
        const clientsRef = collection(db, 'data', regionId, 'countries', countryId, 'clients');
        const clientsSnapshot = await getDocs(clientsRef);

        // Iterar por cada cliente
        for (const clientDoc of clientsSnapshot.docs) {
            const clientId = clientDoc.id;
            const clientInfo = clientDoc.data();

            clientsData[clientId] = {
                info: {
                    id: clientInfo.id || clientId,
                    name: clientInfo.name || clientId
                },
                actions: await exportClientActions(regionId, countryId, clientId)
            };
        }
    } catch (error) {
        console.error(`Error exporting clients for ${countryId}:`, error);
    }

    return clientsData;
}

/**
 * Exporta todas las acciones de un cliente
 */
async function exportClientActions(regionId: string, countryId: string, clientId: string): Promise<any[]> {
    const actionsData: any[] = [];

    try {
        const actionsRef = collection(
            db,
            'data',
            regionId,
            'countries',
            countryId,
            'clients',
            clientId,
            'actions'
        );
        const actionsSnapshot = await getDocs(actionsRef);

        // Incluir TODAS las acciones, incluyendo las eliminadas
        actionsSnapshot.docs.forEach((actionDoc) => {
            const actionData = actionDoc.data();
            actionsData.push({
                id: actionDoc.id,
                ...actionData
            });
        });
    } catch (error) {
        console.error(`Error exporting actions for client ${clientId}:`, error);
    }

    return actionsData;
}

/**
 * Descarga un objeto como archivo JSON
 */
function downloadJSON(data: any, filename: string): void {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Descarga datos como archivo CSV
 */
function downloadCSV(csvContent: string, filename: string): void {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Convierte un array de objetos a formato CSV
 */
function convertToCSV(data: any[]): string {
    if (data.length === 0) return '';

    // Obtener todas las columnas únicas
    const columns = new Set<string>();
    data.forEach(row => {
        Object.keys(row).forEach(key => columns.add(key));
    });

    const headers = Array.from(columns);

    // Delimitador ';' (no ',') para evitar que comas dentro de textos (comentarios, notas...)
    // se interpreten como separador de columnas en Excel.
    const DELIMITER = ';';

    // Crear encabezados
    const csvRows = [headers.join(DELIMITER)];

    // Crear filas
    data.forEach(row => {
        const values = headers.map(header => {
            const value = row[header];
            if (value === null || value === undefined) return '';

            // Escapar comillas y envolver en comillas si contiene el delimitador o saltos de línea
            const stringValue = String(value).replace(/"/g, '""');
            if (stringValue.includes(DELIMITER) || stringValue.includes('\n') || stringValue.includes('"')) {
                return `"${stringValue}"`;
            }
            return stringValue;
        });
        csvRows.push(values.join(DELIMITER));
    });

    return csvRows.join('\n');
}

/**
 * Exporta toda una colección ('data' o 'mitigation') como CSV
 * Aplana todas las acciones en un único CSV con columnas adicionales para región/país/cliente
 */
export async function exportFirestoreDataAsCSV(collectionRoot: string = 'data'): Promise<void> {
    try {
        const allActions: any[] = [];

        // Obtener la estructura
        const structure = await getFullStructure();

        if (!structure.regions) {
            // Intentar listar regiones directamente
            const dataRef = collection(db, collectionRoot);
            const regionsSnapshot = await getDocs(dataRef);

            for (const regionDoc of regionsSnapshot.docs) {
                const regionId = regionDoc.id;
                await collectActionsFromRegion(regionId, allActions, collectionRoot);
            }
        } else {
            // Iterar por cada región en la estructura
            for (const regionId of Object.keys(structure.regions)) {
                await collectActionsFromRegion(regionId, allActions, collectionRoot);
            }
        }

        // Convertir timestamps
        const cleanedActions = convertTimestampsToISO(allActions);

        // Convertir a CSV
        const csvContent = convertToCSV(cleanedActions);

        // Descargar
        downloadCSV(csvContent, `${collectionRoot}_export_${Date.now()}.csv`);

    } catch (error) {
        console.error('Error exporting Firestore data as CSV:', error);
        throw error;
    }
}

/**
 * Recolecta todas las acciones de una región y las añade al array
 */
async function collectActionsFromRegion(regionId: string, actionsArray: any[], collectionRoot: string = 'data'): Promise<void> {
    try {
        const countriesRef = collection(db, collectionRoot, regionId, 'countries');
        const countriesSnapshot = await getDocs(countriesRef);

        for (const countryDoc of countriesSnapshot.docs) {
            const countryId = countryDoc.id;
            const countryInfo = countryDoc.data();

            const clientsRef = collection(db, collectionRoot, regionId, 'countries', countryId, 'clients');
            const clientsSnapshot = await getDocs(clientsRef);

            for (const clientDoc of clientsSnapshot.docs) {
                const clientId = clientDoc.id;
                const clientInfo = clientDoc.data();

                const actionsRef = collection(
                    db,
                    collectionRoot,
                    regionId,
                    'countries',
                    countryId,
                    'clients',
                    clientId,
                    'actions'
                );
                const actionsSnapshot = await getDocs(actionsRef);

                actionsSnapshot.docs.forEach((actionDoc) => {
                    const actionData = actionDoc.data();
                    actionsArray.push({
                        // Información de contexto
                        regionId: regionId,
                        countryId: countryInfo.id || countryId,
                        countryName: countryInfo.name || countryId,
                        countryIsoCode: countryInfo.isoCode || '',
                        clientId: clientInfo.id || clientId,
                        clientName: clientInfo.name || clientId,

                        // Datos de la acción
                        actionId: actionDoc.id,
                        ...actionData
                    });
                });
            }
        }
    } catch (error) {
        console.error(`Error collecting actions from region ${regionId}:`, error);
    }
}

export default {
    exportFirestoreData,
    exportFirestoreDataAsCSV
};
